import Snackbar from '@material-ui/core/Snackbar';
import EditIcon from '@material-ui/icons/Edit';
import MuiAlert from '@material-ui/lab/Alert';
import Button from 'components/Button';
import { APP_MODULES } from 'constants/appModules';
import { PATHS } from 'constants/paths';
import { getLastActiveWellbore, getWellChoice, OptionType } from 'helpers/WellHelper';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import Select, { components, MenuProps, ValueType } from 'react-select';
import { Option } from 'react-select/src/filters';
import { updateCurrentWellAndWellbore } from 'redux/Wells';
import { useSelectAuthorizedWellbores, useSelectCurrentWell } from 'redux/Wells/selectors';
import { WellFullDetailsWithUniqueKeyDTO } from 'types/well';

import styles from './WellSelect.module.scss';
import FavouritePopin from './components/FavouritePopin';

const MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH = 2;

const filterOptions = (candidate: Option, input: string) => {
  if (candidate.data.isActive === false && input.length < MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH) {
    return false;
  }

  return candidate.label.toLowerCase().includes(input.toLowerCase().trim());
};
interface Props {
  currentModule?: APP_MODULES | null;
  wellboresLoading: boolean;
  wellOptions: Array<{
    label: string;
    options: OptionType[];
  }>;
  wells: WellFullDetailsWithUniqueKeyDTO[];
  path: string;
  tightWellAccessible?: boolean;
}

const WellSelect: React.FC<Props> = ({
  currentModule,
  wellboresLoading,
  wellOptions,
  wells,
  path,
  tightWellAccessible,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const well = useSelectCurrentWell();
  const authWellBore = useSelectAuthorizedWellbores();
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [favouritePopinIsOpen, setFavouritePopinIsOpen] = useState<boolean>(false);
  const [selectedWell, setSelectedWell] = useState(well ? getWellChoice(well) : null);
  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedWell(well ? getWellChoice(well) : null);
  }, [well]);

  const redirectToNextPage = useCallback(
    nextWellIdAndTuwi => {
      const { tuwi, wellId } = JSON.parse(nextWellIdAndTuwi);
      const nextWell = wells.find(
        well => (wellId && well.wellId === wellId) || (tuwi && well.tuwi === tuwi),
      );
      const nextWellbore = getLastActiveWellbore(nextWell);
      const nextWellTuwi = nextWell?.tuwi;
      // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
      setSelectedWell(getWellChoice(nextWell!));

      const redirectToHome = (nextWell: any) => {
        const nextWellbore = getLastActiveWellbore(nextWell);
        dispatch(
          updateCurrentWellAndWellbore({
            well: nextWell,
            wellbore: {
              ...nextWellbore,
              well: nextWell,
            },
          }),
        );
        if (nextWell && nextWell.tightWell && !tightWellAccessible) {
          history.push(
            generatePath(PATHS.HOME, {
              wellboreId: nextWell.wellId,
            }),
          );
        } else if (nextWellbore?.wellboreId) {
          history.push(
            generatePath(PATHS.WELL_HOME, {
              wellboreId: nextWellbore.wellboreId,
            }),
          );
        } else {
          history.push(
            generatePath(PATHS.WELL_HOME, {
              wellboreId: nextWell.wellId,
            }),
          );
        }
      };

      if (path === PATHS.LESSONS_LEARNT_WELL) {
        if (nextWell) {
          dispatch(updateCurrentWellAndWellbore({ well: nextWell }));
        }
        history.push(generatePath(PATHS.LESSONS_LEARNT_WELL, {}));
      } else if (path === PATHS.ROP_OPTIMIZATION && nextWellbore?.wellboreId) {
        if (
          authWellBore.find(
            data =>
              data?.wellboreId === nextWellbore?.wellboreId && data?.well && data?.well?.nocQatar,
          )
        ) {
          history.push(
            generatePath(PATHS.ROP_OPTIMIZATION, {
              wellboreId: nextWellbore.wellboreId,
            }),
          );
        } else if (nextWellbore?.wellboreId) {
          history.push(
            generatePath(PATHS.WELL_HOME, {
              wellboreId: nextWellbore.wellboreId,
            }),
          );
        } else if (nextWellTuwi) {
          history.push(generatePath(PATHS.EDDR_OVERVIEW, { tuwi: nextWellTuwi }));
        }
      } else if (path === PATHS.WELL_HOME && nextWellbore?.wellboreId) {
        redirectToHome(nextWell);
      } else if (
        path === PATHS.EDDR_OVERVIEW ||
        path === PATHS.EDDR_SAFETY ||
        path === PATHS.EDDR_BHA ||
        path === PATHS.EDDR_FLUIDS ||
        path === PATHS.EDDR_OPERATIONS_CURVES ||
        path === PATHS.EDDR_WELL_ACTIVITY_HISTORY ||
        path === PATHS.EDDR_OPERATIONS_LOG ||
        path === PATHS.EDDR_TRAJECTORY
      ) {
        if (nextWell) {
          dispatch(updateCurrentWellAndWellbore({ well: nextWell }));
        }
        if (nextWellTuwi) {
          if (nextWell && nextWell.tightWell && !tightWellAccessible) {
            const nextWellbore = getLastActiveWellbore(nextWell);
            dispatch(
              updateCurrentWellAndWellbore({
                well: nextWell,
                wellbore: {
                  ...nextWellbore,
                  well: nextWell,
                },
              }),
            );
            history.push(
              generatePath(PATHS.HOME, {
                wellboreId: nextWell.wellId,
              }),
            );
          } else {
            history.push(generatePath(path, { tuwi: nextWellTuwi }));
          }
        }

        if (!tuwi && nextWellbore?.wellboreId) {
          history.push(
            generatePath(PATHS.WELL_HOME, {
              wellboreId: nextWellbore.wellboreId,
            }),
          );
        }
      } else if (path === PATHS.LESSONS_LEARNT_REGISTER) {
        if (nextWell) {
          dispatch(updateCurrentWellAndWellbore({ well: nextWell }));
        }
        history.push(generatePath(PATHS.LESSONS_LEARNT_REGISTER, {}));
      } else if (nextWellbore?.wellboreId) {
        if (nextWell?.wellId === well?.wellId || nextWell?.wellId !== well?.wellId) {
          redirectToHome(nextWell);
        } else {
          history.push(
            generatePath(PATHS.WELL_HOME, {
              wellboreId: nextWellbore.wellboreId,
            }),
          );
        }
      } else if (nextWellTuwi) {
        history.push(generatePath(PATHS.EDDR_OVERVIEW, { tuwi: nextWellTuwi }));
      }
    },
    [wells, path, history, dispatch, authWellBore, tightWellAccessible, well?.wellId],
  );

  const onWellboreChange = (selectedOption: ValueType<OptionType>) => {
    const { value } = selectedOption as OptionType;
    redirectToNextPage(value);
  };

  const handleManageYourFavouriteOnClick = () => setFavouritePopinIsOpen(true);
  const Menu = (props: MenuProps<OptionType>) => (
    <>
      <components.Menu {...props}>
        <div className={styles.menuContainer}>
          {props.children}
          <hr className={styles.line} />
          <Button
            className={styles.manageYourFavouriteButton}
            onClick={handleManageYourFavouriteOnClick}
          >
            {intl.formatMessage({ id: 'well-select.favouriteWellsManager' })}
            <EditIcon className={styles.editIcon} />
          </Button>
        </div>
      </components.Menu>
    </>
  );

  const handleFavouritePopinOnClose = useCallback(() => setFavouritePopinIsOpen(false), []);

  return (
    <div className={styles.sidebarReactSelectContainer}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => {
          setSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => {
            setSnackbarOpen(false);
          }}
          severity="error"
        >
          {intl.formatMessage({ id: 'header.tuwiNotFoundError' })}
        </MuiAlert>
      </Snackbar>
      <Select
        placeholder={intl.formatMessage({ id: 'header.selectWellPlaceholder' })}
        classNamePrefix="common-react-select"
        value={selectedWell}
        options={wellOptions}
        onChange={onWellboreChange}
        isLoading={wellboresLoading}
        components={{ Menu }}
        filterOption={filterOptions}
      />
      <FavouritePopin open={favouritePopinIsOpen} onClose={handleFavouritePopinOnClose} />
    </div>
  );
};

export default WellSelect;
